import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Typography, Paper,Avatar,CircularProgress,
  Divider,Dialog,DialogTitle,DialogContent,DialogActions,IconButton, Collapse,Grid,Chip,MenuItem,Select,FormControl,InputLabel,
  InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Close as CloseIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { hosturl } from '../libs/Constant';
import { Colors } from '../libs/Colors';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.dark,
  marginRight: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}));

const CreateUserForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const token = localStorage.getItem("token");
  const formRef = React.useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      let response;
      const payload = {
        name: formData.name,
        email: formData.email,
        role: formData.role
      };

      // Only include password if it's not empty
      if (formData.password) {
        payload.password = formData.password;
      }

      if (editMode) {
        response = await axios.patch(`${hosturl}/user/${currentUserId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // For new user creation, password is required
        if (!formData.password) {
          throw new Error('Password is required for new users');
        }
        response = await axios.post(`${hosturl}/user`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (response.status === 201 || response.status === 200) {
        alert(`User ${editMode ? 'updated' : 'created'} successfully!`);
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      if (error.response) {
        setError(error.response.data.message || `Failed to ${editMode ? 'update' : 'create'} user`);
      } else {
        setError(error.message || 'An unexpected error occurred!');
      }
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${hosturl}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.status === 201) {
        setUsers(response.data.result || []);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };  

  const handleEdit = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role || 'user',
      password: '' // Password is intentionally left blank for security
    });
    setCurrentUserId(user._id);
    setEditMode(true);
    setShowForm(true);
    setShowPassword(false); // Reset password visibility when editing
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const handleDelete = (userId) => {
    setCurrentUserId(userId);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await axios.delete(`${hosturl}/user/${currentUserId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 201) {
        alert('User deleted successfully!');
        fetchUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setOpenDialog(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'user' });
    setShowForm(false);
    setEditMode(false);
    setCurrentUserId(null);
    setShowPassword(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const toggleForm = () => {
    setShowForm(!showForm);
    if (showForm) resetForm();
    setError(null);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <StyledPaper elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h5" fontWeight="bold" style={{color:Colors.primaryColor1}}>
            Staff Management
          </Typography>
          <ActionButton 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={toggleForm}
            disabled={showForm && editMode}
          >
            {editMode ? 'Editing User' : 'Add Staff'}
          </ActionButton>
        </Box>

        <Collapse in={showForm}>
        <div ref={formRef}>

          <Paper elevation={0} sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: '12px',
            background: 'linear-gradient(145deg, #f5f7fa, #eef2f5)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                {editMode ? 'Edit User Details' : 'Create New Staff Member'}
              </Typography>
              <IconButton onClick={toggleForm} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={editMode ? 6 : 4}>
                  <TextField
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      style: { borderRadius: '12px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={editMode ? 6 : 4}>
                  <TextField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      style: { borderRadius: '12px' }
                    }}
                  />
                </Grid>
                {!editMode && (
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Role</InputLabel>
                      <Select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        label="Role"
                        required
                        sx={{ borderRadius: '12px' }}
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} md={editMode ? 12 : 4}>
                  <TextField
                    label={editMode ? 'New Password (leave blank to keep current)' : 'Password'}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    required={!editMode}
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      style: { borderRadius: '12px' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <ActionButton 
                  variant="outlined" 
                  onClick={toggleForm} 
                  sx={{ mr: 2 }}
                >
                  Cancel
                </ActionButton>
                <ActionButton 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                >
                  {editMode ? 'Update Staff' : 'Create Staff'}
                </ActionButton>
              </Box>
              {error && (
                <Box sx={{ 
                  mt: 2,
                  p: 2,
                  borderRadius: '8px',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText'
                }}>
                  <Typography>{error}</Typography>
                </Box>
              )}
            </form>
          </Paper>
          </div>
        </Collapse>

        <Divider sx={{ my: 3, borderColor: 'divider' }} />

        <Typography variant="h6" gutterBottom color="text.primary" fontWeight="600" sx={{ mb: 3 }}>
          Staff Members ({users.length})
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : error ? (
          <Box sx={{ 
            p: 3,
            borderRadius: '12px',
            backgroundColor: 'error.light',
            color: 'error.contrastText',
            textAlign: 'center'
          }}>
            <Typography>{error}</Typography>
          </Box>
        ) : users.length > 0 ? (
          <Grid container spacing={3}>
            {users.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user._id}>
                <Paper elevation={0} sx={{ 
                  p: 3, 
                  borderRadius: '16px',
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <UserAvatar>
                      {user.name.charAt(0).toUpperCase()}
                    </UserAvatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600">{user.name}</Typography>
                      <Chip 
                        label={user.role || 'user'} 
                        size="small" 
                        color={user.role === 'admin' ? 'secondary' : 'primary'} 
                        variant="outlined"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          backgroundColor: user.role === 'admin' ? 'secondary.light' : 'primary.light'
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                    color: 'text.secondary'
                  }}>
                    <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">{user.email}</Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: 'text.secondary'
                  }}>
                    <CalendarIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="caption">
                      Joined {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <IconButton 
                    onClick={() => {
                      handleEdit(user);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.light' }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                    <IconButton 
                      onClick={() => handleDelete(user._id)}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': { backgroundColor: 'error.light' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ 
            p: 4,
            textAlign: 'center',
            borderRadius: '12px',
            backgroundColor: 'action.hover'
          }}>
            <PersonIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Staff Members Found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Get started by adding your first staff member
            </Typography>
            <ActionButton 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={toggleForm}
            >
              Add Staff
            </ActionButton>
          </Box>
        )}
      </StyledPaper>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: 2,
            width: '400px',
            maxWidth: '90%'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this staff member? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ActionButton 
            onClick={() => setOpenDialog(false)}
            variant="outlined"
          >
            Cancel
          </ActionButton>
          <ActionButton 
            onClick={confirmDelete} 
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateUserForm;